// Shape relaxation for hex regions
// Implements the "snap and relax" model from Sketchpad

import type { Hex, HexLayout, Point } from "../types.ts";
import {
  hexToKey,
  keyToHex,
  hexNeighbors,
  hexDistance,
} from "../hex/coordinates.ts";

/**
 * Configuration for shape relaxation
 */
export interface RelaxOptions {
  fillHoles?: boolean;       // Fill small internal holes
  removeSpurs?: boolean;     // Remove thin protrusions
  snapToRect?: boolean;      // Try to make more rectangular
  minSize?: number;          // Minimum region size
  smoothness?: number;       // 0-1, how aggressively to smooth (0 = none, 1 = max)
}

const DEFAULT_OPTIONS: RelaxOptions = {
  fillHoles: true,
  removeSpurs: true,
  snapToRect: false,
  minSize: 3,
  smoothness: 0.5,
};

/**
 * Relax a hex region to a cleaner shape.
 * This is the core of the "snap and relax" model.
 */
export function relaxHexRegion(
  hexes: Set<string>,
  options: RelaxOptions = {}
): Set<string> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  if (hexes.size < opts.minSize!) {
    return hexes;
  }

  let result = new Set(hexes);

  // Step 1: Fill small internal holes
  if (opts.fillHoles) {
    result = fillSmallHoles(result, 3); // Fill holes up to 3 hexes
  }

  // Step 2: Remove thin spurs/protrusions
  if (opts.removeSpurs) {
    result = removeSpurs(result);
  }

  // Step 3: Smooth the boundary (fill concavities, remove single-connection hexes)
  if (opts.smoothness! > 0) {
    result = smoothBoundary(result, opts.smoothness!);
  }

  return result;
}

/**
 * Fill small holes inside the region.
 * A "hole" is a group of empty hexes completely surrounded by filled hexes.
 */
function fillSmallHoles(hexes: Set<string>, maxHoleSize: number): Set<string> {
  const result = new Set(hexes);

  // Find the bounding box
  const bounds = getHexBounds(hexes);

  // Find all empty hexes within the bounds
  const emptyHexes = new Set<string>();
  for (let q = bounds.minQ - 1; q <= bounds.maxQ + 1; q++) {
    for (let r = bounds.minR - 1; r <= bounds.maxR + 1; r++) {
      const key = hexToKey({ q, r });
      if (!hexes.has(key)) {
        emptyHexes.add(key);
      }
    }
  }

  // Find connected components of empty hexes
  const visited = new Set<string>();
  const holes: Set<string>[] = [];

  for (const emptyKey of emptyHexes) {
    if (visited.has(emptyKey)) continue;

    // BFS to find connected empty region
    const component = new Set<string>();
    const queue = [keyToHex(emptyKey)];
    let touchesBoundary = false;

    while (queue.length > 0) {
      const current = queue.shift()!;
      const key = hexToKey(current);

      if (visited.has(key) || hexes.has(key)) continue;
      if (!emptyHexes.has(key)) {
        // This empty hex is outside our bounds - touches boundary
        touchesBoundary = true;
        continue;
      }

      visited.add(key);
      component.add(key);

      for (const neighbor of hexNeighbors(current)) {
        const nKey = hexToKey(neighbor);
        if (!visited.has(nKey) && !hexes.has(nKey)) {
          queue.push(neighbor);
        }
      }
    }

    // Only consider it a hole if it doesn't touch the boundary
    if (!touchesBoundary && component.size > 0 && component.size <= maxHoleSize) {
      holes.push(component);
    }
  }

  // Fill the holes
  for (const hole of holes) {
    for (const key of hole) {
      result.add(key);
    }
  }

  return result;
}

/**
 * Remove thin spurs/protrusions from the region.
 * A spur is a hex that only connects to one other hex in the region.
 */
function removeSpurs(hexes: Set<string>): Set<string> {
  const result = new Set(hexes);
  let changed = true;

  // Iteratively remove hexes with only one neighbor until stable
  while (changed) {
    changed = false;
    const toRemove: string[] = [];

    for (const key of result) {
      const hex = keyToHex(key);
      const neighbors = hexNeighbors(hex);
      const connectedCount = neighbors.filter((n) =>
        result.has(hexToKey(n))
      ).length;

      // Remove if only one connection (a spur) and region would remain connected
      if (connectedCount <= 1 && result.size > 3) {
        toRemove.push(key);
      }
    }

    for (const key of toRemove) {
      result.delete(key);
      changed = true;
    }
  }

  return result;
}

/**
 * Smooth the boundary by filling small concavities.
 */
function smoothBoundary(hexes: Set<string>, smoothness: number): Set<string> {
  const result = new Set(hexes);

  // Find boundary hexes (hexes with at least one empty neighbor)
  const boundaryHexes = new Set<string>();
  for (const key of hexes) {
    const hex = keyToHex(key);
    for (const neighbor of hexNeighbors(hex)) {
      if (!hexes.has(hexToKey(neighbor))) {
        boundaryHexes.add(key);
        break;
      }
    }
  }

  // For each empty hex adjacent to the region, count how many neighbors are in the region
  const candidates = new Map<string, number>();
  for (const key of boundaryHexes) {
    const hex = keyToHex(key);
    for (const neighbor of hexNeighbors(hex)) {
      const nKey = hexToKey(neighbor);
      if (!hexes.has(nKey)) {
        const inRegion = hexNeighbors(neighbor).filter((nn) =>
          hexes.has(hexToKey(nn))
        ).length;
        candidates.set(nKey, Math.max(candidates.get(nKey) || 0, inRegion));
      }
    }
  }

  // Fill hexes that have many neighbors in the region (filling concavities)
  const threshold = Math.floor(3 + (1 - smoothness) * 2); // 3-5 based on smoothness
  for (const [key, count] of candidates) {
    if (count >= threshold) {
      result.add(key);
    }
  }

  return result;
}

/**
 * Try to snap a region to a more rectangular shape.
 * Finds the best-fit rectangle and adjusts the region toward it.
 */
export function snapToRectangle(
  hexes: Set<string>,
  strength: number = 0.5
): Set<string> {
  if (hexes.size < 4) return hexes;

  const bounds = getHexBounds(hexes);
  const width = bounds.maxQ - bounds.minQ + 1;
  const height = bounds.maxR - bounds.minR + 1;

  const result = new Set<string>();

  // For each hex in the bounding rectangle, decide if it should be included
  for (let q = bounds.minQ; q <= bounds.maxQ; q++) {
    for (let r = bounds.minR; r <= bounds.maxR; r++) {
      const key = hexToKey({ q, r });
      const wasInOriginal = hexes.has(key);

      // Check how many neighbors are in the original
      const hex = { q, r };
      const neighborsInOriginal = hexNeighbors(hex).filter((n) =>
        hexes.has(hexToKey(n))
      ).length;

      // Include if:
      // - Was in original, OR
      // - Has many neighbors in original (filling gaps) based on strength
      if (wasInOriginal) {
        result.add(key);
      } else if (neighborsInOriginal >= Math.floor(4 - strength * 2)) {
        result.add(key);
      }
    }
  }

  return result;
}

/**
 * Get the bounding box of a hex region
 */
function getHexBounds(hexes: Set<string>): {
  minQ: number;
  maxQ: number;
  minR: number;
  maxR: number;
} {
  let minQ = Infinity, maxQ = -Infinity;
  let minR = Infinity, maxR = -Infinity;

  for (const key of hexes) {
    const hex = keyToHex(key);
    minQ = Math.min(minQ, hex.q);
    maxQ = Math.max(maxQ, hex.q);
    minR = Math.min(minR, hex.r);
    maxR = Math.max(maxR, hex.r);
  }

  return { minQ, maxQ, minR, maxR };
}

/**
 * Analyze a hex region's shape characteristics
 */
export interface ShapeAnalysis {
  size: number;
  width: number;
  height: number;
  aspectRatio: number;
  compactness: number;      // 0-1, how compact (1 = circle-like)
  rectangularity: number;   // 0-1, how rectangular
  convexity: number;        // 0-1, how convex
}

export function analyzeShape(hexes: Set<string>): ShapeAnalysis {
  if (hexes.size === 0) {
    return {
      size: 0,
      width: 0,
      height: 0,
      aspectRatio: 1,
      compactness: 0,
      rectangularity: 0,
      convexity: 0,
    };
  }

  const bounds = getHexBounds(hexes);
  const width = bounds.maxQ - bounds.minQ + 1;
  const height = bounds.maxR - bounds.minR + 1;
  const boundingArea = width * height;

  // Compactness: ratio of area to bounding box
  const compactness = hexes.size / boundingArea;

  // Rectangularity: how well it fills its bounding box
  const rectangularity = compactness;

  // Convexity: ratio of area to convex hull area (simplified)
  // For now, approximate with compactness
  const convexity = Math.sqrt(compactness);

  return {
    size: hexes.size,
    width,
    height,
    aspectRatio: width / height,
    compactness,
    rectangularity,
    convexity,
  };
}

/**
 * Generate a rectangular hex region
 */
export function generateRectangle(
  center: Hex,
  width: number,
  height: number
): Set<string> {
  const hexes = new Set<string>();
  const halfW = Math.floor(width / 2);
  const halfH = Math.floor(height / 2);

  for (let dq = -halfW; dq <= halfW; dq++) {
    for (let dr = -halfH; dr <= halfH; dr++) {
      hexes.add(hexToKey({ q: center.q + dq, r: center.r + dr }));
    }
  }

  return hexes;
}

/**
 * Generate a roughly circular hex region
 */
export function generateCircle(center: Hex, radius: number): Set<string> {
  const hexes = new Set<string>();

  for (let q = -radius; q <= radius; q++) {
    for (let r = -radius; r <= radius; r++) {
      const hex = { q: center.q + q, r: center.r + r };
      if (hexDistance(center, hex) <= radius) {
        hexes.add(hexToKey(hex));
      }
    }
  }

  return hexes;
}

/**
 * Suggest a shape template that best matches the drawn region
 */
export function suggestTemplate(
  hexes: Set<string>
): { type: "rectangle" | "circle" | "irregular"; params: Record<string, number> } {
  const analysis = analyzeShape(hexes);

  if (analysis.rectangularity > 0.7) {
    return {
      type: "rectangle",
      params: { width: analysis.width, height: analysis.height },
    };
  }

  if (analysis.compactness > 0.6 && Math.abs(analysis.aspectRatio - 1) < 0.3) {
    const radius = Math.round(Math.sqrt(analysis.size / Math.PI));
    return {
      type: "circle",
      params: { radius },
    };
  }

  return {
    type: "irregular",
    params: { size: analysis.size },
  };
}
